window.loadMondoSwiper = window.loadMondoSwiper || (() => {
  if (window.Swiper) return Promise.resolve();
  if (window.__mondoSwiperPromise) return window.__mondoSwiperPromise;

  const assets = window.themeAssetUrls || {};

  if (assets.swiperCss && !document.querySelector(`link[href="${assets.swiperCss}"]`)) {
    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = assets.swiperCss;
    document.head.appendChild(stylesheet);
  }

  window.__mondoSwiperPromise = new Promise((resolve, reject) => {
    if (!assets.swiperJs) {
      reject(new Error('Missing Swiper asset URL'));
      return;
    }

    const existingScript = document.querySelector(`script[src="${assets.swiperJs}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = assets.swiperJs;
    script.defer = true;
    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.appendChild(script);
  });

  return window.__mondoSwiperPromise;
});

class MayAccessorize extends HTMLElement {
  connectedCallback() {
    this.barOnly = this.dataset.barOnly === 'true';
    this.mediaQuery = window.matchMedia('(max-width: 749px)');
    this.handleChange = () => this.updateSlider();

    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.handleChange);
    } else {
      this.mediaQuery.addListener(this.handleChange);
    }

    this.setupPopup();
    this.setupTabbar();

    if (!this.barOnly) {
      this.initWhenVisible();
    }
  }

  disconnectedCallback() {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }

    if (this.mediaQuery) {
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.handleChange);
      } else {
        this.mediaQuery.removeListener(this.handleChange);
      }
    }

    this.destroySlider();
    this.disconnectTabbar();
    this.disconnectPopup();
  }

  setupTabbar() {
    this.tabbar = this.querySelector('[data-may-accessorize-tabbar]');
    this.tabbarSlot = this.querySelector('[data-may-accessorize-tabbar-slot]');

    if (!this.tabbar || !this.tabbarSlot) {
      return;
    }

    this.tabbarFixedParent = document.body || this.closest('[id^="shopify-section-"]') || this.parentElement;
    this.tabbarCssVariables = [
      '--may-accessorize-tabbar-bottom',
      '--may-accessorize-tabbar-width',
      '--may-accessorize-tabbar-height',
      '--may-accessorize-tabbar-padding-x',
      '--may-accessorize-tabbar-icon-size',
      '--may-accessorize-tabbar-icon-image-size',
      '--may-accessorize-tabbar-icon-gap',
      '--may-accessorize-tabbar-font-size'
    ];
    this.syncTabbarCssVariables();
    this.tabbarShouldShow = true;
    this.captureMoreInView = false;
    this.tabbarHiddenByCaptureScroll = false;
    this.tabbarPageEndHideThreshold = 100;
    this.lastTabbarScrollY = this.getTabbarScrollY();
    this.lastTabbarScrollDirection = null;
    this.handleHomeHeaderState = (event) => {
      const detail = event.detail || {};
      if (typeof detail.bannerInView === 'boolean') {
        this.tabbarShouldShow = detail.bannerInView === false;
      }
      this.updateTabbar();
    };
    this.handleTabbarScroll = () => this.onTabbarScroll();
    this.handleTabbarResize = () => {
      this.syncTabbarCssVariables();
      this.updateTabbar();
    };
    this.handleTabbarClick = () => {
      const tabbarAction = this.tabbar.dataset.mayAccessorizeTabbarAction || 'popup';
      const tabbarUrl = this.tabbar.dataset.mayAccessorizeTabbarUrl || '';

      if (tabbarAction === 'redirect' && tabbarUrl) {
        const newTab = window.open(tabbarUrl, '_blank', 'noopener,noreferrer');
        if (newTab) newTab.opener = null;
        return;
      }

      if (window.MondoEmailAccess && window.MondoEmailAccess.hasRecord()) {
        window.MondoEmailAccess.redirectToDeposit();
        return;
      }

      this.openPopup();
    };

    window.addEventListener('mondo:home-header-state', this.handleHomeHeaderState);
    window.addEventListener('scroll', this.handleTabbarScroll, { passive: true });
    window.addEventListener('resize', this.handleTabbarResize);
    this.tabbar.addEventListener('click', this.handleTabbarClick);
    this.observeCaptureMoreForTabbar();

    if (window.MondoHomeHeaderState) {
      this.handleHomeHeaderState({ detail: window.MondoHomeHeaderState });
    } else {
      this.updateTabbar();
    }
  }

  disconnectTabbar() {
    if (this.handleHomeHeaderState) {
      window.removeEventListener('mondo:home-header-state', this.handleHomeHeaderState);
    }

    if (this.handleTabbarScroll) {
      window.removeEventListener('scroll', this.handleTabbarScroll);
    }

    if (this.handleTabbarResize) {
      window.removeEventListener('resize', this.handleTabbarResize);
    }

    if (this.tabbar && this.handleTabbarClick) {
      this.tabbar.removeEventListener('click', this.handleTabbarClick);
    }

    if (this.captureMoreObserver) {
      this.captureMoreObserver.disconnect();
      this.captureMoreObserver = null;
    }

    this.attachTabbarToSlot();

    if (this.tabbarFrame) {
      window.cancelAnimationFrame(this.tabbarFrame);
      this.tabbarFrame = null;
    }

    if (this.tabbarHideTimer) {
      window.clearTimeout(this.tabbarHideTimer);
      this.tabbarHideTimer = null;
    }
  }

  setupPopup() {
    this.popup = this.querySelector('[data-may-accessorize-popup]');

    if (!this.popup) {
      return;
    }

    this.popupDialog = this.popup.querySelector('.may-accessorize-popup__dialog');
    this.popupCloseButtons = Array.from(this.popup.querySelectorAll('[data-may-accessorize-popup-close]'));
    this.popupForm = this.popup.querySelector('.may-accessorize-popup__form');
    this.popupEmail = this.popup.querySelector('.may-accessorize-popup__email');
    this.popupSubmit = this.popup.querySelector('.may-accessorize-popup__submit');
    this.popupState = this.popup.querySelector('[data-may-accessorize-popup-state]');
    this.popupMessage = this.popup.querySelector('[data-may-accessorize-popup-message]');
    this.popupStorageKey = 'Is_Access_Email';

    this.handlePopupCloseClick = () => this.closePopup();
    this.handlePopupKeydown = (event) => this.onPopupKeydown(event);
    this.handlePopupSubmit = (event) => this.onPopupSubmit(event);
    this.handlePopupSubmitClick = (event) => {
      if (window.MondoEmailAccess && window.MondoEmailAccess.hasRecord()) {
        event.preventDefault();
        window.MondoEmailAccess.redirectToDeposit(
          this.popupState && this.popupState.dataset.successRedirectUrl
            ? this.popupState.dataset.successRedirectUrl
            : '/pages/deposit'
        );
      }
    };

    this.popupCloseButtons.forEach((button) => {
      button.addEventListener('click', this.handlePopupCloseClick);
    });

    if (this.popupForm) {
      this.popupForm.addEventListener('submit', this.handlePopupSubmit);
    }

    if (this.popupSubmit) {
      this.popupSubmit.addEventListener('click', this.handlePopupSubmitClick);
    }

    this.syncPopupSuccessState();
  }

  disconnectPopup() {
    if (this.popupCloseButtons && this.handlePopupCloseClick) {
      this.popupCloseButtons.forEach((button) => {
        button.removeEventListener('click', this.handlePopupCloseClick);
      });
    }

    if (this.popupForm && this.handlePopupSubmit) {
      this.popupForm.removeEventListener('submit', this.handlePopupSubmit);
    }

    if (this.popupSubmit && this.handlePopupSubmitClick) {
      this.popupSubmit.removeEventListener('click', this.handlePopupSubmitClick);
    }

    if (this.handlePopupKeydown) {
      document.removeEventListener('keydown', this.handlePopupKeydown);
    }

    if (this.popupCloseTimer) {
      window.clearTimeout(this.popupCloseTimer);
      this.popupCloseTimer = null;
    }

  }

  syncPopupSuccessState() {
    if (!this.popupState) {
      return;
    }

    if (window.MondoEmailAccess && window.MondoEmailAccess.hasRecord()) {
      this.showPopupSuccessMessage();
    }
  }

  openPopup() {
    if (!this.popup) {
      return;
    }

    if (this.popupCloseTimer) {
      window.clearTimeout(this.popupCloseTimer);
      this.popupCloseTimer = null;
    }

    this.popupPreviouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.popupOpenRequested = true;
    this.popup.hidden = false;
    this.popup.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', this.handlePopupKeydown);

    window.requestAnimationFrame(() => {
      if (!this.popup || !this.popupOpenRequested) return;
      this.popup.classList.add('is-open');
      if (this.tabbar) {
        this.tabbar.setAttribute('aria-expanded', 'true');
      }

      const initialFocus = this.popupEmail || this.popup.querySelector('[data-may-accessorize-popup-close]');
      if (initialFocus) {
        initialFocus.focus();
      }
    });
  }

  closePopup() {
    if (!this.popup || this.popup.hidden) {
      return;
    }

    this.popup.classList.remove('is-open');
    this.popup.setAttribute('aria-hidden', 'true');
    if (this.tabbar) {
      this.tabbar.setAttribute('aria-expanded', 'false');
    }
    this.popupOpenRequested = false;
    document.removeEventListener('keydown', this.handlePopupKeydown);

    this.popupCloseTimer = window.setTimeout(() => {
      if (!this.popup || this.popup.classList.contains('is-open')) {
        return;
      }

      this.popup.hidden = true;
      this.popupCloseTimer = null;
    }, 240);

    if (this.popupPreviouslyFocused && document.contains(this.popupPreviouslyFocused)) {
      this.popupPreviouslyFocused.focus();
    }
  }

  onPopupKeydown(event) {
    if (!this.popup || this.popup.hidden) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closePopup();
      return;
    }

    if (event.key === 'Tab') {
      this.trapPopupFocus(event);
    }
  }

  trapPopupFocus(event) {
    const focusableElements = this.getPopupFocusableElements();

    if (!focusableElements.length) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  getPopupFocusableElements() {
    if (!this.popup) {
      return [];
    }

    return Array.from(
      this.popup.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
  }

  onPopupSubmit(event) {
    event.preventDefault();

    if (!this.popupSubmit) {
      return;
    }

    if (this.popupSubmit.getAttribute('aria-busy') === 'true') {
      return;
    }

    this.popupSubmit.setAttribute('aria-busy', 'true');
    this.popupSubmit.setAttribute('aria-disabled', 'true');
    this.hidePopupMessage();

    const klaviyoListId = this.popupState ? this.popupState.dataset.klaviyoListId : '';
    const emailValue = this.popupEmail ? this.popupEmail.value : '';

    if (!emailValue || !window.MondoEmailAccess) {
      this.showPopupErrorMessage('Something went wrong. Please try again.');
      this.resetPopupSubmitState();
      return;
    }

    window.MondoEmailAccess.handleSubmit({
      email: emailValue,
      source: 'Homepage Accessorize Popup Form',
      listId: klaviyoListId,
      redirectUrl: this.popupState && this.popupState.dataset.successRedirectUrl
        ? this.popupState.dataset.successRedirectUrl
        : '/pages/deposit'
    })
      .catch((error) => {
        if (!error || error.klaviyoLogged !== true) {
          console.error('[Mondo Klaviyo] Popup subscription failed', error);
        }
        this.showPopupErrorMessage('Something went wrong. Please try again.');
        this.resetPopupSubmitState();
      });
  }

  resetPopupSubmitState() {
    if (!this.popupSubmit) {
      return;
    }

    this.popupSubmit.removeAttribute('aria-busy');
    this.popupSubmit.removeAttribute('aria-disabled');
  }

  hidePopupMessage() {
    if (this.popupMessage) {
      this.popupMessage.hidden = true;
    }

    if (this.popupEmail) {
      this.popupEmail.removeAttribute('aria-describedby');
      this.popupEmail.removeAttribute('aria-invalid');
    }
  }

  showPopupErrorMessage(message) {
    if (!this.popupMessage) {
      return;
    }

    this.popupMessage.textContent = message;
    this.popupMessage.hidden = false;

    if (this.popupEmail) {
      this.popupEmail.setAttribute('aria-invalid', 'true');

      if (this.popupMessage.id) {
        this.popupEmail.setAttribute('aria-describedby', this.popupMessage.id);
      }
    }
  }

  redirectPopupSuccess() {
    const successRedirectUrl = this.popupState && this.popupState.dataset.successRedirectUrl
      ? this.popupState.dataset.successRedirectUrl
      : '/pages/deposit';

    window.location.href = successRedirectUrl;
  }

  showPopupSuccessMessage() {
    if (!this.popupMessage || !this.popupState) {
      return;
    }

    if (this.popupState.dataset.successMessage) {
      this.popupMessage.textContent = this.popupState.dataset.successMessage;
      this.popupMessage.hidden = false;
    }

    if (this.popupEmail) {
      this.popupEmail.removeAttribute('aria-describedby');
      this.popupEmail.removeAttribute('aria-invalid');
    }
  }

  hasStoredPopupSuccess() {
    try {
      return window.sessionStorage.getItem(this.popupStorageKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  storePopupSuccess() {
    try {
      window.sessionStorage.setItem(this.popupStorageKey, 'true');
    } catch (error) {
      // Continue without session persistence if storage is unavailable.
    }
  }

  observeCaptureMoreForTabbar() {
    if (!('IntersectionObserver' in window)) {
      return;
    }

    const captureMoreSection = document.querySelector('.section-main-may-capture-more may-capture-more')
      || document.querySelector('may-capture-more')
      || document.querySelector('.section-main-may-capture-more')
      || document.querySelector('[id*="main_may_capture_more"]');

    if (!captureMoreSection) {
      return;
    }

    this.captureMoreObserver = new IntersectionObserver((entries) => {
      const captureMoreInView = entries.some((entry) => entry.isIntersecting);

      if (this.captureMoreInView === captureMoreInView) {
        return;
      }

      this.captureMoreInView = captureMoreInView;

      if (captureMoreInView) {
        this.tabbarHiddenByCaptureScroll = this.lastTabbarScrollDirection === 'down';
      } else {
        this.tabbarHiddenByCaptureScroll = false;
      }

      this.lastTabbarScrollY = this.getTabbarScrollY();
      this.updateTabbar();
    });

    this.captureMoreObserver.observe(captureMoreSection);
  }

  onTabbarScroll() {
    const currentScrollY = this.getTabbarScrollY();
    const scrollDelta = currentScrollY - this.lastTabbarScrollY;

    this.lastTabbarScrollY = currentScrollY;

    if (this.shouldHideBarOnlyNearPageEnd()) {
      this.hideTabbar();
      return;
    }

    if (Math.abs(scrollDelta) < 4) {
      if (this.barOnly) {
        this.updateTabbar();
      } else if (!this.captureMoreInView) {
        this.requestTabbarUpdate();
      }

      return;
    }

    this.lastTabbarScrollDirection = scrollDelta > 0 ? 'down' : 'up';

    if (!this.captureMoreInView) {
      this.tabbarHiddenByCaptureScroll = false;
      if (this.barOnly) {
        this.updateTabbar();
      } else {
        this.requestTabbarUpdate();
      }
      return;
    }

    this.tabbarHiddenByCaptureScroll = this.lastTabbarScrollDirection === 'down';

    if (this.tabbarHiddenByCaptureScroll) {
      this.hideTabbar();
      return;
    }

    this.updateTabbar();
  }

  requestTabbarUpdate() {
    if (!this.tabbarShouldShow || this.tabbarFrame || (this.captureMoreInView && this.tabbarHiddenByCaptureScroll)) {
      return;
    }

    this.tabbarFrame = window.requestAnimationFrame(() => {
      this.tabbarFrame = null;
      this.updateTabbarPosition();
    });
  }

  updateTabbar() {
    if (!this.tabbar || !this.tabbarSlot) {
      return;
    }

    if (!this.tabbarShouldShow) {
      this.hideTabbar();
      return;
    }

    if (this.shouldHideBarOnlyNearPageEnd()) {
      this.hideTabbar();
      return;
    }

    if (this.captureMoreInView && this.tabbarHiddenByCaptureScroll) {
      this.hideTabbar();
      return;
    }

    if (this.tabbarHideTimer) {
      window.clearTimeout(this.tabbarHideTimer);
      this.tabbarHideTimer = null;
    }

    this.updateTabbarPosition();
    this.tabbar.classList.add('is-visible');
    this.tabbar.removeAttribute('aria-hidden');
    this.tabbar.removeAttribute('tabindex');
  }

  hideTabbar() {
    if (this.tabbarHideTimer) {
      window.clearTimeout(this.tabbarHideTimer);
      this.tabbarHideTimer = null;
    }

    this.tabbar.classList.remove('is-visible');
    this.tabbar.setAttribute('aria-hidden', 'true');
    this.tabbar.setAttribute('tabindex', '-1');

    this.tabbarHideTimer = window.setTimeout(() => {
      const shouldDetach = !this.tabbarShouldShow && !this.barOnly;
      this.tabbarHideTimer = null;

      if (!shouldDetach) return;

      this.tabbar.classList.remove(
        'may-accessorize__tabbar--fixed',
        'may-accessorize__tabbar--docked',
        'may-accessorize__tabbar--position-lock'
      );
      this.attachTabbarToSlot();
    }, 300);
  }

  updateTabbarPosition() {
    if (!this.tabbar || !this.tabbarSlot || !this.tabbarShouldShow) {
      return;
    }

    if (this.barOnly) {
      this.attachTabbarToFixedParent();
      this.tabbar.classList.add('may-accessorize__tabbar--fixed');
      this.tabbar.classList.remove(
        'may-accessorize__tabbar--docked',
        'may-accessorize__tabbar--position-lock'
      );
      return;
    }

    const bottomOffset = this.getTabbarBottomOffset();
    const tabbarHeight = this.tabbar.offsetHeight || this.getTabbarHeight();
    const stickyTop = window.innerHeight - bottomOffset - tabbarHeight;
    const slotTop = this.tabbarSlot.getBoundingClientRect().top;
    const shouldDock = slotTop <= stickyTop;
    const isDocked = this.tabbar.classList.contains('may-accessorize__tabbar--docked');
    const updateTabbarPlacement = () => {
      if (shouldDock) {
        this.attachTabbarToSlot();
      } else {
        this.attachTabbarToFixedParent();
      }

      this.tabbar.classList.toggle('may-accessorize__tabbar--docked', shouldDock);
      this.tabbar.classList.toggle('may-accessorize__tabbar--fixed', !shouldDock);
    };

    if (shouldDock !== isDocked) {
      this.withoutTabbarPositionTransition(() => {
        updateTabbarPlacement();
      });
      return;
    }

    updateTabbarPlacement();
  }

  attachTabbarToFixedParent() {
    if (!this.tabbar || !this.tabbarFixedParent || this.tabbar.parentElement === this.tabbarFixedParent) {
      return;
    }

    this.syncTabbarCssVariables();
    this.tabbarFixedParent.appendChild(this.tabbar);
  }

  attachTabbarToSlot() {
    if (!this.tabbar || !this.tabbarSlot || this.tabbar.parentElement === this.tabbarSlot) {
      return;
    }

    this.tabbarSlot.appendChild(this.tabbar);
  }

  syncTabbarCssVariables() {
    if (!this.tabbar || !this.tabbarCssVariables) {
      return;
    }

    const styles = window.getComputedStyle(this);

    this.tabbarCssVariables.forEach((property) => {
      const value = styles.getPropertyValue(property);

      if (value) {
        this.tabbar.style.setProperty(property, value.trim());
      }
    });
  }

  withoutTabbarPositionTransition(callback) {
    this.tabbar.classList.add('may-accessorize__tabbar--position-lock');
    callback();

    window.requestAnimationFrame(() => {
      this.tabbar.classList.remove('may-accessorize__tabbar--position-lock');
    });
  }

  getTabbarBottomOffset() {
    const styles = window.getComputedStyle(this);
    const rawOffset = styles.getPropertyValue('--may-accessorize-tabbar-bottom');
    const offset = parseFloat(rawOffset);

    return Number.isNaN(offset) ? 20 : offset;
  }

  getTabbarHeight() {
    const styles = window.getComputedStyle(this);
    const rawHeight = styles.getPropertyValue('--may-accessorize-tabbar-height');
    const height = parseFloat(rawHeight);

    return Number.isNaN(height) ? 48 : height;
  }

  getTabbarScrollY() {
    return window.pageYOffset
      || document.documentElement.scrollTop
      || document.body.scrollTop
      || 0;
  }

  shouldHideBarOnlyNearPageEnd() {
    if (!this.barOnly) {
      return false;
    }

    const documentElement = document.documentElement;
    const body = document.body;
    const viewportHeight = window.innerHeight || documentElement.clientHeight || 0;
    const pageHeight = Math.max(
      documentElement.scrollHeight,
      documentElement.offsetHeight,
      documentElement.clientHeight,
      body ? body.scrollHeight : 0,
      body ? body.offsetHeight : 0,
      body ? body.clientHeight : 0
    );
    const remaining = pageHeight - (this.getTabbarScrollY() + viewportHeight);

    return remaining <= this.tabbarPageEndHideThreshold;
  }

  initWhenVisible() {
    if (!('IntersectionObserver' in window)) {
      this.hasEnteredViewport = true;
      this.loadAndUpdate();
      return;
    }

    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        this.visibilityObserver.disconnect();
        this.visibilityObserver = null;
        this.hasEnteredViewport = true;
        this.loadAndUpdate();
      });
    }, { rootMargin: '700px 0px' });

    this.visibilityObserver.observe(this);
  }

  loadAndUpdate() {
    if (!this.mediaQuery.matches) {
      this.updateSlider();
      return;
    }

    window.loadMondoSwiper()
      .then(() => {
        if (!this.isConnected) return;
        this.updateSlider();
      })
      .catch(() => {});
  }

  updateSlider() {
    if (this.barOnly) {
      this.destroySlider();
      return;
    }

    if (!this.mediaQuery.matches) {
      this.destroySlider();
      return;
    }

    if (!window.Swiper) {
      if (this.hasEnteredViewport) this.loadAndUpdate();
      return;
    }

    this.initSlider();
  }

  initSlider() {
    const carousel = this.querySelector('.may-accessorize__carousel');

    if (!carousel || this.swiper) {
      return;
    }

    const mobileGutter = Math.round(window.innerWidth * 0.09);

    this.swiper = new window.Swiper(carousel, {
      slidesPerView: 'auto',
      spaceBetween: 16,
      slidesOffsetAfter: mobileGutter,
      speed: 500,
      watchOverflow: true,
      navigation: {
        prevEl: this.querySelector('.may-accessorize__nav--prev'),
        nextEl: this.querySelector('.may-accessorize__nav--next')
      },
      pagination: {
        el: this.querySelector('.may-accessorize__pagination'),
        clickable: true
      }
    });
  }

  destroySlider() {
    if (!this.swiper) {
      return;
    }

    this.swiper.destroy(true, true);
    this.swiper = null;
  }
}

if (!customElements.get('may-accessorize')) {
  customElements.define('may-accessorize', MayAccessorize);
}

window.MondoOpenSubscribePopup = window.MondoOpenSubscribePopup || (() => {
  const popupHost = document.querySelector('may-accessorize');

  if (popupHost && typeof popupHost.openPopup === 'function') {
    popupHost.openPopup();
    return true;
  }

  const tabbar = document.querySelector('[data-may-accessorize-tabbar]');
  if (tabbar) {
    tabbar.click();
    return true;
  }

  return false;
});
